'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import MainHeader from '@/components/layout/MainHeader';
import { Badge } from '@/components/ui/badge';
import type { Question } from '@/types';

export default function ContactPage() {
  const router = useRouter();
  const [questions, setQuestions] = useState<Omit<Question, 'password'>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/questions')
      .then((res) => res.json())
      .then((data) => setQuestions(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <MainHeader />
      <div className="max-w-[600px] mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">자유롭게 물어보세요</h1>
        <p className="text-sm text-gray-500 mb-4">
          견적 요청은{' '}
          <Link href="/reservation" className="text-[#1A3A5C] underline underline-offset-2 hover:text-[#4DA3FF]">
            예약하기
          </Link>{' '}
          페이지를 이용해주세요.
        </p>
        <div className="flex justify-end mb-6">
          <Link
            href="/contact/new"
            className="px-4 py-2 bg-[#1A3A5C] text-white text-sm rounded-lg font-medium hover:bg-[#15304D] transition-colors"
          >
            질문하기
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-400">불러오는 중...</div>
        ) : questions.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-4">💬</div>
            <p className="text-gray-500">아직 등록된 문의가 없습니다</p>
          </div>
        ) : (
          <div className="space-y-3">
            {questions.map((q) => (
              <button
                key={q.id}
                onClick={() => router.push(`/contact/${q.id}`)}
                className="w-full text-left bg-white border border-gray-200 rounded-xl px-5 py-4 hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">
                      {q.isPrivate && <span className="mr-1.5">🔒</span>}
                      {q.isPrivate ? '비밀글입니다' : q.title}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {new Date(q.createdAt).toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                  <Badge
                    variant={q.status === 'answered' ? 'default' : 'secondary'}
                    className={
                      q.status === 'answered'
                        ? 'bg-green-100 text-green-700 border-green-200'
                        : 'bg-gray-100 text-gray-500 border-gray-200'
                    }
                  >
                    {q.status === 'answered' ? '답변완료' : '답변대기'}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
